import { Datepicker, MbscDatepickerCellClickEvent, MbscTimezonedDate } from "@mobiscroll/react";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import Form from "react-bootstrap/Form";
import {
  AdditionalAttributeInfo,
  AssignFormRequest,
  ClinicalFormDetails,
  ClinicalFormInfo,
  FormAnswersDetails,
  MemberAnswerInfo,
  MemberFormInfo,
  OptionInfo,
  QuestionInfo,
} from "clinical-forms-experience-client";
import { LoadingHelper } from "../components/loader/LoadingHelper";
import { createClinicalFormsApi } from "../api-helpers/ClinicalFormsApiHelper";
import { ClinicalFormsConstants } from "../pages/ClinicalForms/ClinicalFormsConstants";
import ClinicalFormDetailsView from "../pages/ClinicalForms/ClinicalFormDetailsView";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Spinner from "../components/Spinner";
import { formatDateToMMDDYYYY, formatTimeToHHMM, roundToNearest15Minutes } from "../helper/dateFormatter";
import { DatepickerBase } from "@mobiscroll/react/dist/src/core/components/datepicker/datepicker";
import Lookups, { Constants, Withdrawing_Reasons } from "../constants/Lookups";
import { UpdateProgramAndPathway } from "patients-experience-client";
import { createCommonUtilsApi, createWorkflowApi } from "../api-helpers/CommonUtilsApiHelper";
import { WorkflowConstants } from "../helper/WorkflowConstants";
import { WorkflowState } from "common-util-experience-client";
import { Lookup } from "../interfaces/allergy";
import { createPatientApi } from "../api-helpers/PatientApiHelper";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store";
import { setPatientProfileReloadKey } from "../store/slices/patientProfileReload";
import { useAppointmentActivityLogger } from "../hooks/useAppointmentActivityLogger";
import {
  AppointmentActivityAction,
  AppointmentActivityType,
} from "../constants/AppointmentActivity";

const serviceDateKey = "serviceDate";
const serviceTimeKey = "serviceTime";

const notesModules = {
  toolbar: [
    [{ size: [] }, "bold", "italic", "underline", { list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }, "clean"],
  ],
  clipboard: { matchVisual: false },
};
const notesFormats = ["header", "font", "size", "bold", "italic", "underline", "strike", "blockquote", "list", "bullet", "indent"];

// The modal opens at this compact height (roughly the meta + notes "no questions" size) while it
// loads, then grows up to the assembled layout's height. Shared by the loading style and the
// reveal animation so they stay in sync.
const COLLAPSED_HEIGHT = "440px";

type AddCareNoteProps = {
  patientId: string;
  setOffCanvasShow: (data: any) => void;
  editingMemberFormId?: string;
  initialFormId?: string;
  onUpdateCareNotes?: () => void;
  reloadGrid?: () => void,
};

const AddCareNote: React.FC<AddCareNoteProps> = ({
  patientId,
  setOffCanvasShow,
  editingMemberFormId,
  initialFormId,
  reloadGrid
  // onUpdateCareNotes,
}) => {
  const clinicalformsApi = createClinicalFormsApi();

  const [careNoteTypes, setCareNoteTypes] = useState<ClinicalFormInfo[]>([]);

  const [selectedCareNoteType, setSelectedCareNoteType] = useState<string | undefined>(initialFormId);

  const [memberForm, setMemberForm] = useState<MemberFormInfo | undefined>();

  const [allAnswersGiven, setAllAnswersGiven] = useState<boolean>(false);

  const [answers, setAnswers] = useState<MemberAnswerInfo[]>([]);
  const [comments, setComments] = useState<string>("");
  // Bumped after a saved note's comments load so the (uncontrolled) notes editor remounts
  const [notesEditorKey, setNotesEditorKey] = useState(0);
  // Keep track of display behavior for when a care note type has no questions and is just a notes template
  const [hasQuestions, setHasQuestions] = useState<boolean>(true);

  // Reveal timing: the modal opens at a stable height showing a spinner while everything fetches,
  // then reveals the fully-assembled layout in one smooth grow — instead of popping/resizing as
  // each fetch (types, member form + answers, form definition) lands.
  const [formLoaded, setFormLoaded] = useState(false);
  const [revealPhase, setRevealPhase] = useState<"loading" | "revealing" | "open">("loading");
  const contentRef = useRef<HTMLDivElement>(null);

  const [additionalAnswers, setAdditionalAnswers] = useState<Map<string, string>>(new Map());
  const [validated, setValidated] = useState(false);

  const [submitType, setSubmitType] = useState<'submit' | 'save'>();
  const [loading, setLoading] = useState(false);
  const [careNoteServiceDateTime, setCareNoteServiceDateTime] = useState<Date | null>(null);
  const [dateOfService, setDateOfService] = useState<string>("");
  const [timeOfService, setTimeOfService] = useState<string | null>(null);
  const [timeSlotSelected, setTimeSlotSelected] = useState<boolean>(false);
  const [openPicker, setOpenPicker] = useState<boolean>(false);
  const patientProfileReloadKey = useSelector((state: RootState) => state.patientProfileReloadKey.patientProfileReloadKey);
  const dispatch = useDispatch();
  const logAppointmentActivity = useAppointmentActivityLogger();

  useEffect(() => {
    const loadClinicalForms = async () => {
      // No imperative loader here — the modal's reveal gate shows the spinner until everything is ready.
      const response =
        await clinicalformsApi.clinicalFormsControllerGetClinicalForms(
          ClinicalFormsConstants.careNoteType
        );
      setCareNoteTypes(response.data);
    };

    loadClinicalForms();
  }, []);

  useEffect(() => {
    if (!memberForm) {
      // Setting current Date and closest 15 minutes time while creating a new Care Note.
      const closest15Minustes = roundToNearest15Minutes();
      const timezoneOffset = closest15Minustes.getTimezoneOffset();

      const offsetInMs = timezoneOffset * 60 * 1000;
      const utcDateTime = new Date(closest15Minustes.getTime() + offsetInMs);
      const formattedDate = formatDateToMMDDYYYY(closest15Minustes);
      const formattedTime = formatTimeToHHMM(closest15Minustes);

      //Changing the time to be in UTC format so that it shows up correctly for Charting Date Time in Care Note Grid.
      const formattedUTCTime = formatTimeToHHMM(utcDateTime);

      setDateOfService(formattedDate);
      setTimeSlotSelected(true);
      setTimeOfService(formattedTime);

      setAdditionalAnswers((prev) =>
        new Map(prev).set(serviceTimeKey, formattedUTCTime)
      );

      setAdditionalAnswers((prev) =>
        new Map(prev).set(serviceDateKey, formattedDate ?? "")
      );
    }

  }, [memberForm]);

  useEffect(() => {
    if (!editingMemberFormId) {
      return;
    }

    const loadMemberForm = async () => {
      LoadingHelper.showLoader("AddCareNote");
      try {
        const savedMemberForm = (
          await clinicalformsApi.clinicalFormsControllerGetMemberForm(
            editingMemberFormId
          )
        ).data;
        setMemberForm(savedMemberForm);
        setSelectedCareNoteType(savedMemberForm.formId);

        const formAnswers = (
          await clinicalformsApi.clinicalFormsControllerGetMemberAnswers(
            editingMemberFormId
          )
        ).data;
        setAnswers(formAnswers.formAnswers);
        setComments(formAnswers.comments ?? "");
        // Remount the notes editor so it initializes with the just-loaded comments.
        setNotesEditorKey((k) => k + 1);

        if (formAnswers.additionalAnswers) {
          formAnswers.additionalAnswers?.forEach((additionalAnswer) =>
            setAdditionalAnswers(prev => new Map(prev).set(additionalAnswer.name, additionalAnswer.value))
          );
        }
        if (formAnswers.additionalAnswers) {
          formAnswers.additionalAnswers?.forEach((additionalAnswer) => {
            setAdditionalAnswers((prev) =>
              new Map(prev).set(additionalAnswer.name, additionalAnswer.value)
            );
          });
          const utcDate = formAnswers.additionalAnswers.find(answer => answer.name === serviceDateKey)?.value;
          const utcTime = formAnswers.additionalAnswers.find(answer => answer.name === serviceTimeKey)?.value;

          if (utcDate && utcTime) {
            const combinedUtcDateTime = new Date(`${utcDate} ${utcTime}`);
            const localDateTime = new Date(combinedUtcDateTime.getTime() - combinedUtcDateTime.getTimezoneOffset() * 60 * 1000);
            const localDate = formatDateToMMDDYYYY(localDateTime);
            const localTime = formatTimeToHHMM(localDateTime);
            setDateOfService(localDate);
            setTimeOfService(localTime);
            setAdditionalAnswers((prev) => new Map(prev).set(serviceDateKey, utcDate));
            setAdditionalAnswers((prev) => new Map(prev).set(serviceTimeKey, utcTime));
          }
        }

      } finally {
        LoadingHelper.hideLoader("AddCareNote");
      }
    };
    loadMemberForm();
    setAllAnswersGiven(false);
  }, [editingMemberFormId]);

  useEffect(() => {
    if (memberForm && submitType === 'submit') {
      submitCareNote();
    }
  }, [memberForm]);

  const onAnswerChange = async (questionId: string, answer: MemberAnswerInfo) => {
    const existingAnswer = answers.find((a) => a.questionId === questionId);
    if (existingAnswer) {
      existingAnswer.answer = answer.answer;
      existingAnswer.notes = answer.notes
    } else {
      answers.push({ questionId: questionId, answer: answer.answer, notes: answer.notes });
    }
  };

  const saveCareNote = async () => {
    LoadingHelper.showLoader(".add-update-care-note-view");
    setLoading(true);
    try {
      let memberFormRef: MemberFormInfo | undefined;
      if (!memberForm) {
        const request: AssignFormRequest = {
          formId: selectedCareNoteType!,
          source: "manual",
        };
        memberFormRef = (
          await clinicalformsApi.clinicalFormsControllerAssignClinicalForm(
            request,
            patientId
          )
        ).data;
      } else {
        memberFormRef = memberForm;
      }

      const otherAnswers: AdditionalAttributeInfo[] = Array.from(
        additionalAnswers.entries()
      ).map(([key, value]) => {
        return { name: key, value: value! };
      });
      const saveFormAnswersRequest: FormAnswersDetails = {
        formAnswers: answers,
        additionalAnswers: otherAnswers,
        comments: comments,
      };

      await clinicalformsApi.clinicalFormsControllerSaveFormResponses(
        saveFormAnswersRequest,
        memberFormRef!.id
      );
      if (!memberForm) {
        await setMemberForm(memberFormRef);
      }
      if (!memberForm || submitType === 'save') {
        void logAppointmentActivity({
          patientId,
          activityType: AppointmentActivityType.CARE_NOTE,
          refId: memberFormRef!.id,
          action: memberForm
            ? AppointmentActivityAction.UPDATED
            : AppointmentActivityAction.STARTED,
          title: memberFormRef!.title,
          metadata: { formId: memberFormRef!.formId },
        });
      }
      if (submitType === 'submit') {
        await submitCareNote();
      }

      if (submitType === 'save') {
        setOffCanvasShow(false);
      }
      // onUpdateCareNotes();
      reloadGrid!();
    } catch (error: any) {
      console.error("Error while saving care note:", error);
    } finally {
      LoadingHelper.hideLoader(".add-update-care-note-view");
      setLoading(false);
    }
  };

  const submitCareNote = async () => {
    setLoading(true);
    if (!memberForm) {
      return;
    }
    LoadingHelper.showLoader(".add-update-care-note-view");
    try {
      await clinicalformsApi.clinicalFormsControllerUpdateMemberFormStatus(
        { value: ClinicalFormsConstants.submittedStatus },
        memberForm?.id!
      );
      memberForm.status = ClinicalFormsConstants.submittedStatus;
      void logAppointmentActivity({
        patientId,
        activityType: AppointmentActivityType.CARE_NOTE,
        refId: memberForm.id,
        action: AppointmentActivityAction.COMPLETED,
        title: memberForm.title,
        metadata: { formId: memberForm.formId },
      });
      dispatch(setPatientProfileReloadKey(patientProfileReloadKey + 1))
      setMemberForm({ ...memberForm });
      reloadGrid!();

      setOffCanvasShow(false);
    } catch (error: any) {
      console.error("Error while submitting care note:", error);
    } finally {
      setLoading(false);
      LoadingHelper.hideLoader(".add-update-care-note-view");
    }
  }

  const submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setValidated(true);

    const form = event.currentTarget;
    if (!form.checkValidity()) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (!timeOfService || !dateOfService) {
      return;
    }

    if (submitType === "submit" && !allAnswersGiven) {
      return;
    }
    await saveCareNote();
  };

  const generateTimeOptions = () => {
    const options = [];
    const start = new Date();
    start.setHours(0, 0, 0, 0); // Start at 12:00 AM
    const end = new Date();
    end.setHours(23, 45, 0, 0); // End at 11:45 PM

    while (start <= end) {
      const timeString = start.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      });
      options.push(<option key={timeString}>{timeString}</option>);
      start.setMinutes(start.getMinutes() + 15); // Increment by 15 minutes
    }

    return options;
  };

  const handleTimeSlotChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedTime = e.target.value;
    setTimeSlotSelected(true);
    setTimeOfService(selectedTime);
    const localDate = careNoteServiceDateTime ?? new Date(dateOfService);
    const localDateTime = new Date(
      `${localDate!.toDateString()} ${selectedTime}`
    );
    const timezoneOffset = localDateTime.getTimezoneOffset();

    const offsetInMs = timezoneOffset * 60 * 1000;
    const utcDateTime = new Date(localDateTime.getTime() + offsetInMs);

    const formattedDate = formatDateToMMDDYYYY(utcDateTime);
    const formattedTime = formatTimeToHHMM(utcDateTime);

    setAdditionalAnswers((prev) =>
      new Map(prev).set(serviceTimeKey, formattedTime)
    );

    setAdditionalAnswers((prev) =>
      new Map(prev).set(serviceDateKey, formattedDate ?? "")
    );
  };


  // Everything needed to render the final layout is in: care note types (for the title), the saved
  // note (when editing/viewing), and the form definition (questions). Notes-only types still fire
  // onHasQuestionsChange, so formLoaded becomes true for them too.
  const dataReady =
    careNoteTypes.length > 0 &&
    (editingMemberFormId ? !!memberForm : true) &&
    formLoaded;

  // Once the data is ready, move from the spinner to the reveal animation.
  useEffect(() => {
    if (revealPhase === "loading" && dataReady) {
      setRevealPhase("revealing");
    }
  }, [revealPhase, dataReady]);

  // Safety net: never let the spinner hang. If the data signals don't all arrive, reveal anyway.
  useEffect(() => {
    if (revealPhase !== "loading") return;
    const id = setTimeout(() => setRevealPhase("revealing"), 10000);
    return () => clearTimeout(id);
  }, [revealPhase]);

  // Animate the grow: measure the assembled layout's height, snap back to the loading height
  // before paint, then transition up/down to the measured height and release to auto.
  useLayoutEffect(() => {
    if (revealPhase !== "revealing") return;
    const el = contentRef.current;
    if (!el) {
      setRevealPhase("open");
      return;
    }
    const target = el.getBoundingClientRect().height; // final (capped) height of the real layout
    el.style.height = COLLAPSED_HEIGHT; // start compact — set before paint, so there's no flash to final
    el.style.overflow = "hidden";
    void el.offsetHeight; // force reflow so the next change animates
    el.style.transition = "height 0.4s ease";
    el.style.height = `${target}px`;
    const done = () => {
      el.removeEventListener("transitionend", done);
      el.style.transition = "";
      el.style.height = "";
      el.style.overflow = "";
      setRevealPhase("open");
    };
    el.addEventListener("transitionend", done);
    return () => el.removeEventListener("transitionend", done);
  }, [revealPhase]);

  const isReadOnly = memberForm?.status === ClinicalFormsConstants.submittedStatus;

  const notesEditor = (
    <div className="cn-notes">
      <label className="form-label fs-12 fw-semibold mb-1">
        Additional Notes
      </label>
      <ReactQuill
        key={notesEditorKey}
        theme="snow"
        placeholder="Type Notes..."
        modules={isReadOnly ? { toolbar: false } : notesModules}
        formats={notesFormats}
        defaultValue={comments}
        onChange={(value) => setComments(value)}
        readOnly={isReadOnly}
        className={isReadOnly ? "disabled-ql-editor" : ""}
      />
    </div>
  );

  return (
    <>
      <Form
        noValidate
        validated={validated}
        onSubmit={submitHandler}
      >
        <div
          ref={contentRef}
          className={`add-update-care-note-view${revealPhase === "loading" ? " is-loading" : ""}`}
          style={revealPhase === "loading" ? { height: COLLAPSED_HEIGHT } : undefined}
        >
          {revealPhase === "loading" && (
            <div className="cn-load-spinner">
              <div className="spinner-border text-secondary" role="status">
                <span className="visually-hidden">Loading…</span>
              </div>
            </div>
          )}
          {/* LEFT PANE */}
          <div className="cn-pane cn-pane-left p-4">
            <div className="row g-2 mb-3">
              <div className="col-6">
                <label
                  htmlFor="targetDate"
                  className="form-label fs-12 fw-semibold mb-1"
                >
                  Date of Service
                </label>
                <div className={`mobiscroll ${validated ? (!dateOfService ? "invalid-input" : "valid-input") : ""}`}>
                  <div className="mobiscroll__calendar">
                    <Datepicker
                      controls={["calendar"]}
                      calendarType="month"
                      theme="windows"
                      themeVariant="light"
                      placeholder="MM/DD/YYYY"
                      display="bubble" // Can be 'inline', 'center', 'bubble', etc.
                      touchUi={true} // Enables touch-friendly UI
                      buttons={[]} // Disables the Set and Cancel buttons
                      value={dateOfService}
                      showOnClick={false}
                      isOpen={openPicker}
                      onClose={() => { setOpenPicker(false) }}
                      onChange={(event) => {
                        setAdditionalAnswers(prev => new Map(prev).set(
                          serviceDateKey,
                          event.valueText ?? ""
                        ));
                        setDateOfService(event.valueText!);
                        setTimeOfService(null);
                        setCareNoteServiceDateTime(new Date(event.valueText!));
                      }}
                      disabled={
                        memberForm?.status === ClinicalFormsConstants.submittedStatus
                      }
                      required
                    ></Datepicker>
                  </div>
                  <div onClick={() => setOpenPicker(!openPicker)} className="mobiscroll__calendar-icon">
                    <span className="material-symbols-outlined fs-18" >
                      calendar_month
                    </span>
                  </div>
                </div>
              </div>
              <div className="col-6">
                <label
                  htmlFor="selectTime"
                  className="form-label fs-12 fw-semibold mb-1"
                >
                  Time of Service
                </label>
                <select
                  className="form-select form-select-sm "
                  name="taskCategory"
                  value={timeOfService || ''}
                  onChange={(event) => {
                    setAdditionalAnswers(prev => new Map(prev).set(
                      serviceTimeKey,
                      event.target.value
                    ));
                    handleTimeSlotChange(event);
                  }}
                  disabled={
                    memberForm?.status === ClinicalFormsConstants.submittedStatus
                  }
                  required
                >
                  <option value={''} selected={!timeSlotSelected} disabled>Select Time</option>
                  {generateTimeOptions()}
                </select>
              </div>
            </div>
            {/* Notes present in the left sidebar when the form has questions */}
            {hasQuestions && notesEditor}
          </div>
          {/* RIGHT PANE */}
          <div className="cn-pane cn-pane-right p-4">
            {selectedCareNoteType ? (
              <>
                <div className="cn-form-header">
                  <div className="cn-form-header__eyebrow">Care Note Type</div>
                  <h2 className="cn-form-header__title">
                    {careNoteTypes.find((t) => t.id === selectedCareNoteType)?.title}
                  </h2>
                </div>
                <ClinicalFormDetailsView
                  formId={selectedCareNoteType!}
                  comments={comments}
                  answers={answers}
                  onAnswersChange={onAnswerChange}
                  hideComments={true}
                  onAllAnswersGivenChange={(allAnswersGiven) =>
                    setAllAnswersGiven(allAnswersGiven)
                  }
                  onHasQuestionsChange={(hq) => {
                    setHasQuestions(hq);
                    setFormLoaded(true);
                  }}
                  onCommentsChange={setComments}
                  readOnly={
                    memberForm?.status === ClinicalFormsConstants.submittedStatus
                  }
                ></ClinicalFormDetailsView>
                {/* Care note has no questions so editor is the main content of the right pane */}
                {!hasQuestions && notesEditor}
              </>
            ) : (
              <div className="cn-pane-empty text-light fs-14">
                Select a care note type to begin.
              </div>
            )}
          </div>
        </div>
        <div className="offcanvas-footer w-100 border-top d-flex justify-content-between px-4 py-3">
              {(memberForm &&
                memberForm.status === ClinicalFormsConstants.submittedStatus) && (
                  <button
                    className="ms-auto btn btn-sm btn-outline-secondary rounded-pill px-5 fs-13"
                    type="button"
                    onClick={() => setOffCanvasShow(false)}
                  >
                    Close
                  </button>
                )}
              {(!memberForm ||
                memberForm.status !== ClinicalFormsConstants.submittedStatus) && (
                  <button
                    className="ms-2 btn btn-sm btn-outline-secondary rounded-pill px-5 fs-13"
                    type="reset"
                    onClick={() => setOffCanvasShow(false)}
                  >
                    Discard
                  </button>
                )}
              {(!memberForm || memberForm.status !== ClinicalFormsConstants.submittedStatus) &&
                (<div className="d-flex gap-2">
                  <button
                    className="btn btn-sm btn-secondary rounded-pill px-5 fs-13"
                    type="submit"
                    disabled={loading}
                    onClick={() => setSubmitType("save")}
                  >
                    Save as Draft
                  </button>
                  <button
                    className="btn btn-sm btn-secondary rounded-pill px-5 fs-13"
                    type="submit"
                    disabled={loading || !allAnswersGiven}
                    onClick={() => setSubmitType("submit")}
                  >
                    Submit
                  </button>
                </div>)}
        </div>
      </Form>
    </>
  );
};

export default AddCareNote;
